import { useCallback, useMemo, useState } from "react";
import { isPatchEmpty, KPI_SHARED_FIELDS, newDraftRow, patchField } from "./helpers";

/**
 * Unsaved edits for the grid, in two buckets.
 *
 * - `patches` holds, per saved row, only the fields whose value differs from
 *   the server's. A cell edited back to its original value leaves the patch,
 *   so an empty patch is the definition of a clean row and "is this cell
 *   dirty?" never needs a comparison at the call site.
 * - `drafts` holds rows that do not exist yet. They are wholly unsaved, so
 *   they carry their edits inline instead of a patch.
 *
 * The hook deliberately has no effect watching the module or financial year:
 * switching context is a user action, and the page clears drafts in that
 * handler. Nothing here reacts to a changing `rows` prop.
 *
 * @param {object[]} rows saved rows from useKpiRows
 */
export function useRowDrafts(rows) {
  const [patches, setPatches] = useState({});
  const [drafts, setDrafts] = useState([]);

  const savedById = useMemo(() => {
    const map = {};
    rows.forEach((row) => {
      map[row.id] = row;
    });
    return map;
  }, [rows]);

  const yearRowsByKpi = useMemo(() => {
    const map = new Map();
    rows.forEach((row) => {
      if (row.questionId == null) return;
      if (!map.has(row.questionId)) map.set(row.questionId, []);
      map.get(row.questionId).push(row);
    });
    return map;
  }, [rows]);

  const setField = useCallback(
    (rowId, field, value) => {
      const saved = savedById[rowId];
      if (!saved) {
        setDrafts((current) =>
          current.map((draft) => (draft.id === rowId ? { ...draft, [field]: value } : draft)),
        );
        return;
      }
      const targets = KPI_SHARED_FIELDS.includes(field)
        ? yearRowsByKpi.get(saved.questionId) || [saved]
        : [saved];
      setPatches((current) => {
        const updated = { ...current };
        targets.forEach((row) => {
          const next = patchField(row, current[row.id], field, value);
          if (isPatchEmpty(next)) delete updated[row.id];
          else updated[row.id] = next;
        });
        return updated;
      });
    },
    [savedById, yearRowsByKpi],
  );

  /**
   * Rewrites one field across many rows, each from its own current value —
   * the selection bar's append modes, where "add this tag" has to respect what
   * every row already carries rather than overwriting it. A plain overwrite is
   * `() => value`.
   *
   * @param {(string|number)[]} rowIds
   * @param {string} field
   * @param {(rowId: string|number) => *} computeNext
   */
  const bulkUpdateField = useCallback(
    (rowIds, field, computeNext) => {
      rowIds.forEach((rowId) => setField(rowId, field, computeNext(rowId)));
    },
    [setField],
  );


  const addDraft = useCallback((title, context) => {
    const draft = newDraftRow(title, context);
    setDrafts((current) => [...current, draft]);
    return draft.id;
  }, []);

  const removeDraft = useCallback((rowId) => {
    setDrafts((current) => current.filter((draft) => draft.id !== rowId));
  }, []);

  const discardAll = useCallback(() => {
    setPatches({});
    setDrafts([]);
  }, []);

  return {
    patches,
    drafts,
    dirtyCount: Object.keys(patches).length + drafts.length,
    newCount: drafts.length,
    setField,
    bulkUpdateField,
    addDraft,
    removeDraft,
    discardAll,
  };
}
